import {
  Component,
  DoCheck,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SelectRecipientComponent } from './partials/select-recipient/select-recipient.component';
import { MessageContentsComponent } from './partials/message-contents/message-contents.component';
import { MessagePreviewComponent } from './partials/message-preview/message-preview.component';
import { MessagePhoneNumbersModalComponent } from './partials/message-phone-numbers-modal/message-phone-numbers-modal.component';
import { MessageSelectedOrganizationsComponent } from './partials/message-selected-organizations/message-selected-organizations.component';
import { MessageSelectedSegmentsComponent } from './partials/message-selected-segments/message-selected-segments.component';
import { MessageSelectedTagsComponent } from './partials/message-selected-tags/message-selected-tags.component';
import { MessageSelectedContactsComponent } from './partials/message-selected-contacts/message-selected-contacts.component';
import { AssignContactsToGroupComponent } from '../../../../components/modals/assign-contacts-to-group/assign-contacts-to-group.component';
import { AddOrganizationsModalComponent } from '../../../../components/tables/add-organizations-modal/add-organizations-modal.component';
import { AddTagsModalComponent } from '../../../../components/tables/add-tags-modal/add-tags-modal.component';
import { AddSegmentsModalComponent } from '../../../../components/tables/add-segments-modal/add-segments-modal.component';
import {
  IContact,
  IOrganization,
  ITag,
} from '../../../../types/contactModule';
import { IEntitySegmentQuery } from '../../../../components/organization/segments/segment-utilities';
import { LocalCsvService } from '../../../../services/local-csv/local-csv.service';
import { CsvPhoneNumbersUploadComponent } from '../../../../components/inputs/csv-phone-numbers-upload/csv-phone-numbers-upload.component';
import { MessageSelectedCsvResultsComponent } from './partials/message-selected-csv-results/message-selected-csv-results.component';

import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { SavePhoneNumbersComponentComponent } from './partials/save-phone-numbers-component/save-phone-numbers-component.component';
import {
  BroadcastMessageService,
  IRecipientCountPayload,
} from '../../../../services/broadcast/broadcast.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { PreviewMessageDetailsComponent } from './partials/preview-message-details/preview-message-details.component';
import { MessageService } from '../../../../services/message/message.service';
import { ContactsService } from '../../../../services/contacts/contacts.service';
import { ComposeAsideComponent } from './partials/compose-aside/compose-aside.component';
import { formatCount, formatCountdown, smsParts, SmsParts } from '../../../../../utils/sms';
import { describeSchedule, ScheduleSummary, zonedInstant } from './compose-schedule';
import { Subscription } from 'rxjs';

/** Above this many numbers the live count leaves them out of the request. */
const COUNT_NUMBERS_LIMIT = 5000;

/** Recipients a link can hand to compose: `?tags=uuid,uuid&numbers=+267…`. */
interface LinkPrefill {
  organizations: string[];
  tags: string[];
  segments: string[];
  numbers: string[];
  rejectedNumbers: number;
}

@Component({
  selector: 'create-new-message',
  standalone: true,
  imports: [
    SelectRecipientComponent,
    MessageContentsComponent,
    MessagePreviewComponent,
    MessagePhoneNumbersModalComponent,
    MessageSelectedOrganizationsComponent,
    MessageSelectedSegmentsComponent,
    MessageSelectedTagsComponent,
    MessageSelectedContactsComponent,
    AssignContactsToGroupComponent,
    AddOrganizationsModalComponent,
    AddTagsModalComponent,
    AddSegmentsModalComponent,
    ReactiveFormsModule,
    CsvPhoneNumbersUploadComponent,
    MessageSelectedCsvResultsComponent,
    SavePhoneNumbersComponentComponent,
    PreviewMessageDetailsComponent,
    ComposeAsideComponent,
  ],
  templateUrl: './create-new-message.component.html',
  styleUrl: './create-new-message.component.css',
})
export class CreateNewMessageComponent implements OnInit, DoCheck, OnDestroy {
  constructor() {}
  broadcastMessageService = inject(BroadcastMessageService);
  localCSVService = inject(LocalCsvService);
  messageService = inject(MessageService);
  private contactsService = inject(ContactsService);
  private route = inject(ActivatedRoute);
  private host: ElementRef<HTMLElement> = inject(ElementRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private zone = inject(NgZone);

  builder = inject(FormBuilder);
  router = inject(Router);

  submitted: boolean = false;
  private noRecipientsTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private feedbackTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private countTimeoutId: ReturnType<typeof setTimeout> | undefined;
  /** Keeps "goes out in 3 h 10 min" current while the page is open. */
  private clockId: ReturnType<typeof setInterval> | undefined;
  private subscriptions = new Subscription();

  currentView: 'writing' | 'preview' = 'writing';

  updateView(view: 'writing' | 'preview') {
    this.currentView = view;
    this.sendError = '';
    if (view === 'preview') this.refreshAudienceCount(true);
    this.scrollToTop();
    this.focusTitle();
  }

  // MODALS LOGIC----------------------------------------------------------------------------------------------------
  // phone numbers modal
  showPhoneNumbersModal: boolean = false;
  openPhoneNumbersModal() {
    this.showPhoneNumbersModal = true;
  }
  closePhoneNumbersModal() {
    this.showPhoneNumbersModal = false;
  }

  //  contacts modal
  showContactSelectedModal: boolean = false;
  openContactSelectedModal() {
    this.showContactSelectedModal = true;
  }
  closeContactSelectedModal() {
    this.showContactSelectedModal = false;
  }

  // csv files modal
  showCSVFilesModal: boolean = false;
  openCSVFilesModal() {
    this.showCSVFilesModal = true;
  }
  closeCSVFilesModal() {
    this.showCSVFilesModal = false;
  }

  // tags modal
  showTagSelectedModal: boolean = false;
  openTagSelectedModal() {
    this.showTagSelectedModal = true;
  }
  closeTagSelectedModal() {
    this.showTagSelectedModal = false;
  }
  // segments modal
  showSegmentSelectedModal: boolean = false;
  openSegmentSelectedModal() {
    this.showSegmentSelectedModal = true;
  }
  closeSegmentSelectedModal() {
    this.showSegmentSelectedModal = false;
  }
  // organizations modal
  showOrganizationSelectedModal: boolean = false;
  openOrganizationSelectedModal() {
    this.showOrganizationSelectedModal = true;
  }
  closeOrganizationSelectedModal() {
    this.showOrganizationSelectedModal = false;
  }

  // MODALS related to SELECTED RECIPIENTS------------------------------------------------------------------------------------------------------------
  // contacts
  showAddContactsModal: boolean = false;
  openAddContactsModal() {
    this.showAddContactsModal = true;
  }
  closeAddContactsModal() {
    this.showAddContactsModal = false;
  }

  // oranizations
  showAddOrganizationsModal: boolean = false;
  openAddOrganizationsModal() {
    this.showAddOrganizationsModal = true;
  }
  closeAddOrganizationsModal() {
    this.showAddOrganizationsModal = false;
  }

  // tags
  showAddTagsModal: boolean = false;
  openAddTagsModal() {
    this.showAddTagsModal = true;
  }
  closeAddTagsModal() {
    this.showAddTagsModal = false;
  }

  // segments
  showAddSegmentsModal: boolean = false;
  openAddSegmentsModal() {
    this.showAddSegmentsModal = true;
  }
  closeAddSegmentsModal() {
    this.showAddSegmentsModal = false;
  }

  // Save phone numbers------ this is used during submission, where there exist manually added phone numbers or CSV added phone numbers
  showSavePhoneNumbersModal: boolean = false;
  openSavePhoneNumbersModal() {
    this.showSavePhoneNumbersModal = true;
  }
  closeSavePhoneNumbersModal() {
    this.showSavePhoneNumbersModal = false;
  }

  // STATES AND GETTERS------------------------------------------------------------------------------------------------------------
  // phone numbers--------------------------------------------------------------------------------------------
  phoneNumbers: string[] = [];
  addPhoneNumber(phoneNumber: string) {
    // remove duplicates
    if (!this.phoneNumbers.includes(phoneNumber))
      this.phoneNumbers = [...this.phoneNumbers, phoneNumber];
  }
  removePhoneNumber(phoneNumber: string) {
    this.phoneNumbers = this.phoneNumbers.filter(
      (number) => number !== phoneNumber
    );
  }

  get phoneNumbersCount(): number {
    return this.phoneNumbers.length;
  }

  // CSV Phone numbers --------------------------------------------------------------------------------------------------------
  // CSV upload modal control
  openCsvUploadModal: boolean = false;
  openCsvUpload() {
    this.openCsvUploadModal = true;
  }
  closeCsvUpload() {
    this.openCsvUploadModal = false;
  }

  /** Every number from the uploaded files, in upload order. */
  get csvPhoneNumbers(): string[] {
    const numbers: string[] = [];
    this.localCSVService.getAllFormattedFileData.forEach((file) =>
      file.submitted.forEach((entry) => numbers.push(entry.phoneNumber))
    );
    return numbers;
  }

  // Contacts------------------------------------------------------------------------------------------------------------
  selectedContacts: IContact[] = [];
  addSelectedContacts(contacts: IContact[]) {
    this.selectedContacts = contacts;
    this.scheduleAudienceCount();
  }
  get selectedContactsCount(): number {
    return this.selectedContacts.length;
  }
  removeContact(contact: IContact) {
    this.selectedContacts = this.selectedContacts.filter(
      (c) => c.id !== contact.id
    );
  }
  removeSelectedContacts(contacts: IContact[]) {
    const contactIdsToRemove = contacts.map((c) => c.id);
    this.selectedContacts = this.selectedContacts.filter(
      (c) => !contactIdsToRemove.includes(c.id)
    );
  }
  removeAllSelection() {
    this.selectedContacts = [];
  }

  // tags------------------------------------------------------------------------------------------------------------
  selectedTags: ITag[] = [];
  addSelectedTags(tags: ITag[]) {
    this.selectedTags = tags;
    this.scheduleAudienceCount();
  }
  get selectedTagsCount(): number {
    return this.selectedTags.length;
  }
  removeTag(tag: ITag) {
    this.selectedTags = this.selectedTags.filter((t) => t.id !== tag.id);
  }
  removeSelectedTags(tags: ITag[]) {
    const tagIdsToRemove = tags.map((t) => t.id);
    this.selectedTags = this.selectedTags.filter(
      (t) => !tagIdsToRemove.includes(t.id)
    );
  }

  // segments------------------------------------------------------------------------------------------------------------
  selectedSegments: IEntitySegmentQuery[] = [];
  addSelectedSegments(segments: IEntitySegmentQuery[]) {
    this.selectedSegments = segments;
    this.scheduleAudienceCount();
  }
  get selectedSegmentsCount(): number {
    return this.selectedSegments.length;
  }
  removeSegment(segment: IEntitySegmentQuery) {
    this.selectedSegments = this.selectedSegments.filter(
      (s) => s.id !== segment.id
    );
  }
  removeSelectedSegments(segments: IEntitySegmentQuery[]) {
    const segmentIdsToRemove = segments.map((s) => s.id);
    this.selectedSegments = this.selectedSegments.filter(
      (s) => !segmentIdsToRemove.includes(s.id)
    );
  }

  // organizations------------------------------------------------------------------------------------------------------------
  selectedOrganizations: IOrganization[] = [];
  addSelectedOrganizations(organizations: IOrganization[]) {
    this.selectedOrganizations = organizations;
    this.scheduleAudienceCount();
  }
  get selectedOrganizationsCount(): number {
    return this.selectedOrganizations.length;
  }
  removeOrganization(organization: IOrganization) {
    this.selectedOrganizations = this.selectedOrganizations.filter(
      (o) => o.id !== organization.id
    );
  }
  removeSelectedOrganizations(organizations: IOrganization[]) {
    const organizationIdsToRemove = organizations.map((o) => o.id);
    this.selectedOrganizations = this.selectedOrganizations.filter(
      (o) => !organizationIdsToRemove.includes(o.id)
    );
  }

  // recipient getters
  get atleastOneRecipientSelected(): boolean {
    return (
      Boolean(this.phoneNumbers.length) ||
      Boolean(this.selectedOrganizations.length) ||
      Boolean(this.selectedSegments.length) ||
      Boolean(this.selectedTags.length) ||
      Boolean(this.selectedContacts.length) ||
      Boolean(this.localCSVService.getAllFormattedFileData.length)
    );
  }

  // phone numbers added
  get phoneNumberAdded(): boolean {
    return (
      Boolean(this.phoneNumbers.length) ||
      Boolean(this.localCSVService.getAllFormattedFileData.length)
    );
  }

  // form values------------------------------------------------------------------------------------------------------------
  messageContentForm = this.builder.group({
    senderId: ['', Validators.required],
    messageSubject: ['', Validators.required],
    template: [''],
    messageBody: ['', Validators.required],
    messageType: ['Instant', Validators.required],
    timeZone: [''],
    date: [''],
    time: [''],
  });

  get getMessageType() {
    return this.messageContentForm.value.messageType || '';
  }

  get isScheduledSend(): boolean {
    return this.getMessageType === 'Scheduled';
  }

  // LIVE SUMMARIES------------------------------------------------------------------------------------------------------------
  private partsFor = '';
  private partsCache: SmsParts = smsParts('');

  /** Parts, encoding and length of the body as typed (worked out once per change). */
  get bodyParts(): SmsParts {
    const body = this.messageContentForm.value.messageBody || '';
    if (body !== this.partsFor) {
      this.partsFor = body;
      this.partsCache = smsParts(body);
    }
    return this.partsCache;
  }

  private scheduleKey = '';
  private scheduleCache: ScheduleSummary = describeSchedule(null, null, null, () => '');

  /**
   * The schedule in words, resolved in the chosen time zone. Recomputed when
   * the date, time or zone change, and every 30 seconds for the countdown.
   */
  get schedule(): ScheduleSummary {
    const { date, time, timeZone } = this.messageContentForm.value;
    const key = `${date}|${time}|${timeZone}|${Math.floor(Date.now() / 30000)}`;
    if (key !== this.scheduleKey) {
      this.scheduleKey = key;
      this.scheduleCache = describeSchedule(date, time, timeZone, (value) => formatCountdown(value));
    }
    return this.scheduleCache;
  }

  /** Deduplicated audience size from the server, or null before the first count. */
  audienceCount: number | null = null;
  audienceCountLoading = false;
  audienceCountError = '';
  /** True when the count leaves out a very long number list and adds it locally. */
  audienceCountApprox = false;
  private lastCountKey = '';
  private lastAudienceShape = '';
  private countSubscription?: Subscription;

  /** Plain sum of what was picked, before duplicates are removed. */
  get audienceParts(): { label: string; count: number }[] {
    const csvFiles = this.localCSVService.getAllFormattedFileData.length;
    const parts = [
      { label: this.plural(this.phoneNumbers.length, 'number', 'numbers'), count: this.phoneNumbers.length },
      { label: this.plural(csvFiles, 'file', 'files'), count: csvFiles },
      { label: this.plural(this.selectedContacts.length, 'contact', 'contacts'), count: this.selectedContacts.length },
      { label: this.plural(this.selectedTags.length, 'tag', 'tags'), count: this.selectedTags.length },
      {
        label: this.plural(this.selectedOrganizations.length, 'organization', 'organizations'),
        count: this.selectedOrganizations.length,
      },
      { label: this.plural(this.selectedSegments.length, 'segment', 'segments'), count: this.selectedSegments.length },
    ];
    return parts.filter((part) => part.count > 0);
  }

  /** "3 numbers, 2 tags and 1 file". */
  get audienceBreakdown(): string {
    const items = this.audienceParts.map((part) => `${formatCount(part.count)} ${part.label}`);
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  }

  /** Estimated SMS: parts × recipients, once both are known. */
  get estimatedSms(): number | null {
    if (this.audienceCount === null || !this.bodyParts.parts) return null;
    return this.bodyParts.parts * this.audienceCount;
  }

  formatCount = formatCount;

  /** "about " when a very long number list was added to the count locally. */
  countApproxPrefix(): string {
    return this.audienceCountApprox ? 'about ' : '';
  }

  plural(count: number, one: string, many: string): string {
    return count === 1 ? one : many;
  }

  /** ["a", "b", "c"] → "a, b and c". */
  private listInWords(items: string[]): string {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  }

  ngOnInit() {
    this.setupConditionalValidators();

    const query = this.route.snapshot.queryParamMap;
    const typeParam = query.get('type');
    const itemsParam = query.get('items');
    const templateParam = query.get('template');

    if (typeParam == 'duplicate') {
      this.applyDuplicate();
    }

    if (itemsParam) {
      const contacts = this.parseJson(itemsParam);
      if (Array.isArray(contacts) && contacts.length) {
        this.selectedContacts = contacts;
      }
    }

    if (templateParam) {
      const template = this.parseJson(templateParam);
      if (template) {
        this.messageContentForm.patchValue({
          template: template.id,
          messageBody: template.content,
        });
      }
    }

    if (this.messageService.selectedOrganizations.length) {
      this.selectedOrganizations = [...this.messageService.selectedOrganizations];
      this.messageService.updateSelectedOrganizations([]);
    }
    if (this.messageService.selectedTags.length) {
      this.selectedTags = [...this.messageService.selectedTags];
      this.messageService.updateSelectedTags([]);
    }
    if (this.messageService.selectedSegments.length) {
      this.selectedSegments = [...this.messageService.selectedSegments];
      this.messageService.updateSelectedSegments([]);
    }

    this.applyLinkPrefill(query);

    if (this.isBrowser) {
      // Outside Angular's zone so the app still settles; re-enter only to refresh the view.
      this.zone.runOutsideAngular(() => {
        this.clockId = setInterval(() => this.zone.run(() => {}), 30000);
      });
    }
  }

  /** Recount when the audience changes shape: something added, removed or a file uploaded. */
  ngDoCheck(): void {
    const shape = [
      this.phoneNumbers.length,
      this.selectedContacts.length,
      this.selectedTags.length,
      this.selectedOrganizations.length,
      this.selectedSegments.length,
      this.localCSVService.getAllFormattedFileData.map((file) => file.batchId).join(','),
    ].join('|');
    if (shape !== this.lastAudienceShape) {
      this.lastAudienceShape = shape;
      this.scheduleAudienceCount();
    }
  }

  private parseJson(value: string): any {
    try {
      return JSON.parse(value);
    } catch {
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch {
        return null;
      }
    }
  }

  // DUPLICATE ------------------------------------------------------------------------------------------------------------
  /**
   * Fill the form from a message being duplicated: navigation state
   * `{ message }` or `{ duplicate }` first, then the older `messageDetails`
   * storage key.
   */
  private applyDuplicate() {
    if (!this.isBrowser) return;
    let messageDetails: any = null;
    try {
      // Details sends `{ duplicate }`; `{ message }` is the documented key.
      messageDetails = history.state?.message ?? history.state?.duplicate ?? null;
    } catch {
      messageDetails = null;
    }
    if (!messageDetails) {
      try {
        const stored = localStorage.getItem('messageDetails');
        messageDetails = stored ? JSON.parse(stored) : null;
      } catch {
        messageDetails = null;
      }
    }
    if (!messageDetails) return;

    const scheduled = /^sched/i.test(String(messageDetails.messageType ?? ''));
    this.messageContentForm.patchValue({
      messageSubject: messageDetails.messageSubject ?? '',
      messageBody: messageDetails.content ?? messageDetails.message ?? '',
      messageType: scheduled ? 'Scheduled' : 'Instant',
      timeZone: messageDetails.scheduleTimeZone ?? '',
      date: this.toDateInput(messageDetails.scheduleDate, messageDetails.scheduleTimeZone),
      time: (messageDetails.scheduleTime ?? '').slice(0, 5),
      senderId: messageDetails.from ?? messageDetails.senderId ?? '',
    });
  }

  /** "2025-10-13T07:00:00Z" → "2025-10-13" as seen in the message's zone. */
  private toDateInput(value: unknown, zone?: string | null): string {
    const text = String(value ?? '');
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
    const date = new Date(text);
    if (!text || Number.isNaN(date.getTime())) return '';
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: zone || undefined,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(date);
      const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
      return `${get('year')}-${get('month')}-${get('day')}`;
    } catch {
      return text.slice(0, 10);
    }
  }

  // LINK PREFILL ------------------------------------------------------------------------------------------------------------
  /** What the link handed over, for the note above the editor. */
  prefillNote = '';

  /**
   * `?organizations=uuid,uuid&tags=uuid&segments=uuid&numbers=+267…,+267…`
   * (or `numbers` as an array in navigation state, for long lists) prefill
   * the audience. Nothing is ever sent from a link.
   */
  private applyLinkPrefill(query: ParamMap) {
    const list = (key: string) =>
      query
        .getAll(key)
        .join(',')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);

    let stateNumbers: string[] = [];
    let stateSubject: unknown = null;
    if (this.isBrowser) {
      try {
        const fromState = history.state?.numbers;
        if (Array.isArray(fromState)) stateNumbers = fromState.map((value) => String(value));
        stateSubject = history.state?.subject ?? null;
      } catch {
        stateNumbers = [];
        stateSubject = null;
      }
    }

    // `?subject=` (or `subject` in navigation state) fills an empty subject only.
    const rawSubject = query.get('subject') ?? stateSubject;
    const subject = typeof rawSubject === 'string' ? rawSubject.trim() : '';
    const subjectControl = this.messageContentForm.controls.messageSubject;
    const subjectAdded = Boolean(subject) && !String(subjectControl.value ?? '').trim();
    if (subjectAdded) subjectControl.setValue(subject);

    const rawNumbers = [...list('numbers'), ...stateNumbers];
    // A Set keeps this linear: a resend can hand over 25,000 numbers.
    const unique = new Set<string>();
    let rejected = 0;
    rawNumbers.forEach((value) => {
      const number = this.normaliseNumber(value);
      if (!number) rejected += 1;
      else unique.add(number);
    });
    const numbers = [...unique];

    const prefill: LinkPrefill = {
      organizations: list('organizations'),
      tags: list('tags'),
      segments: list('segments'),
      numbers,
      rejectedNumbers: rejected,
    };

    if (numbers.length) {
      this.phoneNumbers = [...new Set([...this.phoneNumbers, ...numbers])];
    }
    this.prefillGroups(prefill);

    const handed: string[] = [];
    if (numbers.length) handed.push(`${formatCount(numbers.length)} ${this.plural(numbers.length, 'number', 'numbers')}`);
    if (prefill.tags.length) handed.push(`${prefill.tags.length} ${this.plural(prefill.tags.length, 'tag', 'tags')}`);
    if (prefill.organizations.length)
      handed.push(`${prefill.organizations.length} ${this.plural(prefill.organizations.length, 'organization', 'organizations')}`);
    if (prefill.segments.length)
      handed.push(`${prefill.segments.length} ${this.plural(prefill.segments.length, 'segment', 'segments')}`);
    if (subjectAdded) handed.push('the subject');

    if (handed.length || rejected) {
      const added = handed.length ? `Added ${this.listInWords(handed)} from the link you followed.` : '';
      const skipped = rejected
        ? ` ${rejected} ${this.plural(rejected, 'entry was', 'entries were')} not a valid phone number and ${this.plural(rejected, 'was', 'were')} left out.`
        : '';
      this.prefillNote = `${added}${skipped} Nothing is sent until you review it.`.trim();
    }
  }

  /** "+267 71 234 567", " 26771234567" (a + that arrived as a space) → "+26771234567". */
  private normaliseNumber(value: string): string | null {
    const compact = String(value ?? '').replace(/[\s().-]/g, '');
    if (!compact) return null;
    const withPlus = compact.startsWith('+') ? compact : compact.startsWith('00') ? `+${compact.slice(2)}` : `+${compact}`;
    return /^\+[1-9]\d{6,14}$/.test(withPlus) ? withPlus : null;
  }

  /** Placeholders first so the uuids are in the payload at once; names fill in after a lookup. */
  private prefillGroups(prefill: LinkPrefill) {
    const placeholder = (uuid: string, noun: string): any => ({
      id: uuid,
      uuid,
      name: `Loading ${noun}…`,
      description: '',
    });

    const newTags = prefill.tags.filter((uuid) => !this.selectedTags.some((tag) => tag.uuid === uuid));
    const newOrgs = prefill.organizations.filter(
      (uuid) => !this.selectedOrganizations.some((org) => org.uuid === uuid)
    );
    const newSegments = prefill.segments.filter(
      (uuid) => !this.selectedSegments.some((segment) => segment.uuid === uuid)
    );

    if (newTags.length) {
      this.selectedTags = [...this.selectedTags, ...newTags.map((uuid) => placeholder(uuid, 'tag'))];
      if (this.isBrowser) {
        this.subscriptions.add(
          this.contactsService.searchTags({ 'uuid.in': newTags, size: newTags.length }).subscribe({
            next: ({ items }) => (this.selectedTags = this.mergeFound(this.selectedTags, items, 'tag')),
            error: () => (this.selectedTags = this.mergeFound(this.selectedTags, [], 'tag')),
          })
        );
      }
    }

    if (newOrgs.length) {
      this.selectedOrganizations = [
        ...this.selectedOrganizations,
        ...newOrgs.map((uuid) => placeholder(uuid, 'organization')),
      ];
      if (this.isBrowser) {
        this.subscriptions.add(
          this.contactsService.searchOrganizations({ 'uuid.in': newOrgs, size: newOrgs.length }).subscribe({
            next: ({ items }) =>
              (this.selectedOrganizations = this.mergeFound(this.selectedOrganizations, items, 'organization')),
            error: () =>
              (this.selectedOrganizations = this.mergeFound(this.selectedOrganizations, [], 'organization')),
          })
        );
      }
    }

    if (newSegments.length) {
      this.selectedSegments = [
        ...this.selectedSegments,
        ...newSegments.map((uuid) => placeholder(uuid, 'segment')),
      ];
      if (this.isBrowser) {
        this.subscriptions.add(
          this.broadcastMessageService.lookupSegments(newSegments).subscribe({
            next: (items) => (this.selectedSegments = this.mergeFound(this.selectedSegments, items, 'segment')),
            error: () => (this.selectedSegments = this.mergeFound(this.selectedSegments, [], 'segment')),
          })
        );
      }
    }
  }

  /** Swap placeholders for the records found; ones not found keep their uuid under a plain name. */
  private mergeFound<T extends { uuid?: string | null; name?: string | null }>(
    current: T[],
    found: any[],
    noun: string
  ): T[] {
    const byUuid = new Map((found || []).filter((item) => item?.uuid).map((item) => [item.uuid, item]));
    return current.map((item) => {
      if (!String(item.name ?? '').startsWith('Loading ')) return item;
      const match = item.uuid ? byUuid.get(item.uuid) : undefined;
      return (match ?? { ...item, name: `Unnamed ${noun}` }) as T;
    });
  }

  // LIVE AUDIENCE COUNT ------------------------------------------------------------------------------------------------------------
  private buildCountPayload(): { payload: IRecipientCountPayload; extraNumbers: number } {
    const numbers = Array.from(new Set([...this.phoneNumbers, ...this.csvPhoneNumbers]));
    const tooMany = numbers.length > COUNT_NUMBERS_LIMIT;
    return {
      payload: {
        phoneNumbers: tooMany ? [] : numbers,
        contactIds: this.selectedContacts.map((c) => String(c.uuid ?? '')).filter(Boolean),
        tagIds: this.selectedTags.map((t) => String(t.uuid ?? '')).filter(Boolean),
        organizationIds: this.selectedOrganizations.map((o) => String(o.uuid ?? '')).filter(Boolean),
        segmentIds: this.selectedSegments.map((s) => String(s.uuid ?? '')).filter(Boolean),
      },
      extraNumbers: tooMany ? numbers.length : 0,
    };
  }

  scheduleAudienceCount() {
    if (!this.isBrowser) return;
    if (this.countTimeoutId) clearTimeout(this.countTimeoutId);
    this.countTimeoutId = setTimeout(() => {
      this.countTimeoutId = undefined;
      this.refreshAudienceCount();
    }, 700);
  }

  /** Ask the server how many people the audience reaches once duplicates go. */
  refreshAudienceCount(force = false) {
    if (!this.isBrowser) return;
    if (this.countTimeoutId) {
      clearTimeout(this.countTimeoutId);
      this.countTimeoutId = undefined;
    }

    if (!this.atleastOneRecipientSelected) {
      this.countSubscription?.unsubscribe();
      this.audienceCount = null;
      this.audienceCountLoading = false;
      this.audienceCountError = '';
      this.audienceCountApprox = false;
      this.lastCountKey = '';
      return;
    }

    const { payload, extraNumbers } = this.buildCountPayload();
    const key = JSON.stringify(payload) + `|${extraNumbers}`;
    if (!force && key === this.lastCountKey && !this.audienceCountError) return;
    this.lastCountKey = key;

    this.countSubscription?.unsubscribe();
    this.audienceCountLoading = true;
    this.audienceCountError = '';
    const onlyNumbers =
      !payload.contactIds?.length &&
      !payload.tagIds?.length &&
      !payload.organizationIds?.length &&
      !payload.segmentIds?.length &&
      !payload.phoneNumbers?.length;

    if (onlyNumbers) {
      // A very long number list and nothing else: no need to ask the server.
      this.audienceCount = extraNumbers;
      this.audienceCountApprox = false;
      this.audienceCountLoading = false;
      return;
    }

    this.countSubscription = this.broadcastMessageService.countRecipients(payload).subscribe({
      next: (total) => {
        this.audienceCount = total + extraNumbers;
        this.audienceCountApprox = extraNumbers > 0;
        this.audienceCountLoading = false;
      },
      error: () => {
        // Never leave the previous audience's figure on screen as if it were current.
        this.audienceCount = null;
        this.audienceCountApprox = false;
        this.audienceCountLoading = false;
        this.audienceCountError = 'Could not count recipients right now.';
        this.lastCountKey = '';
      },
    });
  }

  private setupConditionalValidators() {
    const messageTypeControl = this.messageContentForm.get('messageType');
    const timeZoneControl = this.messageContentForm.get('timeZone');
    const dateControl = this.messageContentForm.get('date');
    const timeControl = this.messageContentForm.get('time');

    // Subscribe to messageType changes
    const typeChanges = messageTypeControl?.valueChanges.subscribe((type) => {
      const isScheduled = type && type !== 'Instant';

      // Apply or clear validators
      if (isScheduled) {
        timeZoneControl?.setValidators([Validators.required]);
        dateControl?.setValidators([Validators.required]);
        timeControl?.setValidators([Validators.required]);
      } else {
        timeZoneControl?.clearValidators();
        dateControl?.clearValidators();
        timeControl?.clearValidators();
      }

      // Update validity immediately
      timeZoneControl?.updateValueAndValidity({ emitEvent: false });
      dateControl?.updateValueAndValidity({ emitEvent: false });
      timeControl?.updateValueAndValidity({ emitEvent: false });
    });
    if (typeChanges) this.subscriptions.add(typeChanges);

    // Trigger initial validation
    this.updateScheduleValidators();
  }

  private updateScheduleValidators() {
    const type = this.messageContentForm.get('messageType')?.value;
    const isScheduled = type && type !== 'Instant';

    const controls = ['timeZone', 'date', 'time'] as const;
    controls.forEach((field) => {
      const control = this.messageContentForm.get(field);
      if (isScheduled) {
        control?.setValidators(Validators.required);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  // Optional: Call this when form is reset or type changes externally
  overrideFormType(type: 'Instant' | 'Scheduled') {
    this.messageContentForm.patchValue({ messageType: type });
    // Validators will auto-update via valueChanges
  }

  // feedback error controllers
  successMsg: string = '';
  errorMsg: string = '';
  noRecipientsErrorMsg = '';
  /** Why the last send failed, shown beside the send button. */
  sendError = '';

  updateNoRecipientsError(message: string) {
    this.noRecipientsErrorMsg = message;
    if (this.noRecipientsTimeoutId) {
      clearTimeout(this.noRecipientsTimeoutId);
    }
    this.noRecipientsTimeoutId = setTimeout(() => {
      this.noRecipientsErrorMsg = '';
      this.noRecipientsTimeoutId = undefined;
    }, 4000);
  }

  // update feedback message
  updateFeedbackMsg(type: 'success' | 'error', message: string) {
    if (this.feedbackTimeoutId) {
      clearTimeout(this.feedbackTimeoutId);
    }
    if (type === 'success') {
      this.successMsg = message;
      this.feedbackTimeoutId = setTimeout(() => {
        this.successMsg = '';
        this.feedbackTimeoutId = undefined;
      }, 4000);
      this.errorMsg = '';
    } else {
      this.errorMsg = message;
      this.feedbackTimeoutId = setTimeout(() => {
        this.errorMsg = '';
        this.feedbackTimeoutId = undefined;
      }, 4000);
      this.successMsg = '';
    }
  }

  /** The zone-correct instant for a scheduled send, or null for an instant one. */
  private get scheduleInstant(): string | null {
    if (!this.isScheduledSend) return null;
    return this.schedule.at ? this.schedule.at.toISOString() : null;
  }

  // this are the details that will be passed onto the save phone numbers modal so the child can access the info from the parent before submitting the messgae
  get getMessageDetails() {
    return {
      senderID: this.messageContentForm.value.senderId as string,
      messageSubject: this.messageContentForm.value.messageSubject!,
      messageBody: this.messageContentForm.value.messageBody!,
      template: this.messageContentForm.value.template!,
      phoneNumbers: this.phoneNumbers, //this represents the manually added phone
      organizations: this.selectedOrganizations.map((el) => el.uuid as string),
      segments: this.selectedSegments.map((el) => el.uuid as string),
      tags: this.selectedTags.map((el) => el.uuid as string),
      contacts: this.selectedContacts.map((el) => el.uuid as string),
      messageType: this.messageContentForm.value.messageType! as
        | 'Instant'
        | 'Scheduled',
      timeZone: this.messageContentForm.value.timeZone!,
      date: this.messageContentForm.value.date!,
      time: this.messageContentForm.value.time!,
      scheduleInstant: this.scheduleInstant,
    };
  }

  /** What still needs doing before the message can be reviewed, in plain words. */
  get missingFields(): string[] {
    const controls = this.messageContentForm.controls;
    const missing: string[] = [];
    if (controls.senderId.invalid) missing.push('a sender ID');
    if (controls.messageSubject.invalid) missing.push('a subject');
    if (!this.atleastOneRecipientSelected) missing.push('at least one recipient');
    if (controls.messageBody.invalid) missing.push('the message');
    if (this.isScheduledSend && (controls.timeZone.invalid || controls.date.invalid || controls.time.invalid)) {
      missing.push('a send date, time and time zone');
    }
    return missing;
  }

  get missingSummary(): string {
    const items = this.missingFields;
    if (!items.length) return '';
    const list = items.length === 1 ? items[0] : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
    return `Add ${list} to continue.`;
  }

  /** A scheduled time that has already passed. */
  get scheduleInPast(): boolean {
    return this.isScheduledSend && this.schedule.past;
  }

  /** Same check against the clock right now, not the 30-second display cache. */
  private scheduleHasPassed(): boolean {
    if (!this.isScheduledSend) return false;
    const { date, time, timeZone } = this.messageContentForm.value;
    const at = zonedInstant(date, time, timeZone);
    if (!at) return false;
    if (at.getTime() <= Date.now()) {
      // Refresh the summary so the "time has passed" error shows at once.
      this.scheduleKey = '';
      return true;
    }
    return false;
  }

  /** The one problem the action bar shows (and announces), or '' when there is none. */
  get barError(): string {
    if (this.sendError) return this.sendError;
    if (this.submitted && this.missingSummary) return this.missingSummary;
    if (this.submitted && this.scheduleInPast) return 'That send time has passed. Pick a time in the future.';
    return '';
  }

  /** Check everything, then show the review step. */
  goToReview() {
    this.submitted = true;
    this.updateScheduleValidators();

    const passed = this.scheduleHasPassed();
    if (this.messageContentForm.invalid || !this.atleastOneRecipientSelected || passed || this.scheduleInPast) {
      if (!this.atleastOneRecipientSelected) {
        this.updateNoRecipientsError('Add at least one recipient: a number, a file, contacts or a saved group.');
      }
      this.focusFirstProblem();
      return;
    }
    this.updateView('preview');
  }

  // if no phone number added flow will open the save phone number modal
  handleSubmit() {
    this.submitted = true;
    this.sendError = '';

    // Re-evaluate all validators before submit
    this.updateScheduleValidators();

    const passed = this.scheduleHasPassed();
    if (this.messageContentForm.invalid || !this.atleastOneRecipientSelected || passed || this.scheduleInPast) {
      if (!this.atleastOneRecipientSelected) {
        this.updateNoRecipientsError('Add at least one recipient: a number, a file, contacts or a saved group.');
      }
      if (this.currentView == 'preview') {
        this.currentView = 'writing';
        this.focusTitle();
      }
      this.focusFirstProblem();
      return;
    } else {
      if (this.phoneNumberAdded) {
        // show this if there were some phone numbers added; either via CSV or manual phone number addition
        this.openSavePhoneNumbersModal();
      } else {
        this.broadcastMessageService
          .sendMessage({
            senderID: this.messageContentForm.value.senderId as string,
            messageSubject: this.messageContentForm.value.messageSubject!,
            messageBody: this.messageContentForm.value.messageBody!,
            messageType: this.messageContentForm.value.messageType! as
              | 'Instant'
              | 'Scheduled',
            template: this.messageContentForm.value.template!,
            timeZone: this.messageContentForm.value.timeZone!,
            date: this.messageContentForm.value.date!,
            time: this.messageContentForm.value.time!,
            scheduleInstant: this.scheduleInstant,
            phoneNumbers: this.phoneNumbers,
            organizations: this.selectedOrganizations.map(
              (el) => el.uuid as string
            ),
            segments: this.selectedSegments.map((el) => el.uuid as string),
            tags: this.selectedTags.map((el) => el.uuid as string),
            contacts: this.selectedContacts.map((el) => el.uuid as string),
            saveContacts: false,
          })
          .subscribe({
            next: () => {
              this.router.navigate(['organization', 'messages']);
            },
            error: (err) => {
              this.sendError = this.describeSendError(err);
            },
          });
      }
    }
  }

  private describeSendError(err: any): string {
    const reason = err?.error?.message || err?.error?.detail || err?.error?.title;
    const action = this.isScheduledSend ? 'scheduled' : 'sent';
    if (typeof reason === 'string' && reason.trim()) {
      return `The message was not ${action}: ${reason.trim()}`;
    }
    if (err?.status === 0) {
      return `The message was not ${action}. Check your connection and try again.`;
    }
    return `The message was not ${action}. Try again, and contact support if it keeps failing.`;
  }

  handleBack() {
    if (this.currentView === 'preview') {
      this.updateView('writing');
    } else {
      this.router.navigate(['organization', 'messages']);
    }
  }

  handleCancel() {
    this.router.navigate(['organization', 'messages']);
  }

  /** Bring the first field with an error into view once the errors render. */
  private focusFirstProblem() {
    if (!this.isBrowser) return;
    setTimeout(() => {
      const root = this.host.nativeElement as HTMLElement;
      const target =
        root.querySelector<HTMLElement>('.bk-field.is-invalid') ||
        root.querySelector<HTMLElement>('[data-problem="true"]');
      if (!target) return;
      target.scrollIntoView({ block: 'center' });
      target.querySelector<HTMLElement>('input, select, textarea, button')?.focus({ preventScroll: true });
    });
  }

  private scrollToTop() {
    if (!this.isBrowser) return;
    setTimeout(() => (this.host.nativeElement as HTMLElement).scrollIntoView?.({ block: 'start' }));
  }

  /**
   * The button just pressed is gone with the old view; move focus to the new
   * title so keyboard and screen reader users land at the start of it.
   */
  private focusTitle() {
    if (!this.isBrowser) return;
    setTimeout(() =>
      (this.host.nativeElement as HTMLElement)
        .querySelector<HTMLElement>('#compose-title')
        ?.focus({ preventScroll: true })
    );
  }

  ngOnDestroy() {
    if (this.noRecipientsTimeoutId) {
      clearTimeout(this.noRecipientsTimeoutId);
    }
    if (this.feedbackTimeoutId) {
      clearTimeout(this.feedbackTimeoutId);
    }
    if (this.countTimeoutId) {
      clearTimeout(this.countTimeoutId);
    }
    if (this.clockId) {
      clearInterval(this.clockId);
    }
    this.countSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
    // Uploaded files belong to this message only; the next compose starts empty.
    while (this.localCSVService.getAllFormattedFileData.length) {
      if (!this.localCSVService.removeFromAllFormattedUsingIndex(0)) break;
    }
  }
}
